import React, { useEffect, useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { dashApi } from "../api";
import { STAGE_LABEL } from "../utils";

// Stages a rep is allowed to choose from a magic-link email.
// Mirrors backend PIPELINE_STAGES_FOR_REPS.
const SELECTABLE = ["contacted", "qualified", "quote_sent", "negotiation", "won", "lost"];

/**
 * Dialog: send a magic-link email to a rep asking them to update the
 * lead's stage with one click. Default rep is the currently-assigned rep
 * (if any). Token + email sent on confirm.
 */
export default function RequestUpdateDialog({ open, onClose, doc, onSent }) {
  const [reps, setReps] = useState([]);
  const [repId, setRepId] = useState(doc?.assigned_to || "");
  const [stages, setStages] = useState(["contacted", "qualified", "quote_sent", "negotiation", "won"]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    dashApi.get("/users/assignable")
      .then(({ data }) => {
        setReps(data || []);
        // Default-pick the assigned rep, otherwise the first sales_rep, otherwise first.
        if (!repId && data?.length) {
          const assigned = data.find((u) => u.id === doc?.assigned_to);
          const fallback = data.find((u) => u.role === "sales_rep") || data[0];
          setRepId((assigned || fallback).id);
        }
      })
      .catch(() => setReps([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.assigned_to]);

  const toggleStage = (s) => {
    setStages((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]
    );
  };

  const handleSend = async () => {
    if (!repId || stages.length === 0) return;
    setBusy(true);
    try {
      const { data } = await dashApi.post(
        `/submissions/${doc.id}/request-update`,
        { rep_user_id: repId, stages, message: message.trim() || undefined }
      );
      toast.success(`Update request sent to ${data.rep_email}`);
      onSent?.(data);
      onClose();
    } catch (e) {
      toast.error(e.message || "Failed to send request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="request-update-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-4 w-4" /> Request a status update
          </DialogTitle>
          <DialogDescription>
            Sends the rep a one-click email. They pick the current stage; the
            dashboard updates automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="req-rep" className="text-xs">Send to</Label>
            <select
              id="req-rep"
              value={repId}
              onChange={(e) => setRepId(e.target.value)}
              disabled={busy}
              className="mt-1 w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white disabled:bg-slate-50"
              data-testid="req-update-rep-select"
            >
              {reps.length === 0 && <option value="">No reps available</option>}
              {reps.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email} ({u.role.replace("_", " ")})
                </option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-xs">Stages they can pick from</Label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {SELECTABLE.map((s) => {
                const on = stages.includes(s);
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleStage(s)}
                    disabled={busy}
                    data-testid={`req-update-stage-${s}`}
                    className={`px-2.5 py-1 rounded-md border text-xs font-medium transition-colors ${
                      on
                        ? "bg-blue-50 border-blue-300 text-blue-800"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    } disabled:opacity-50`}
                  >
                    {STAGE_LABEL[s] || s}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              The rep will see one button per selected stage in the email.
            </p>
          </div>

          <div>
            <Label htmlFor="req-msg" className="text-xs">Optional note (visible in the email)</Label>
            <Textarea
              id="req-msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Hey — any movement since last week?"
              rows={2}
              maxLength={500}
              disabled={busy}
              data-testid="req-update-message"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSend}
            disabled={busy || !repId || stages.length === 0}
            data-testid="req-update-send-btn"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
            Send email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
